import type {ReactNode} from 'react'
import {createContext, use, useEffect} from 'react'
import {createPortal} from 'react-dom'

/**
 * Portal targets exposed by `CompactSidePanelDialog`. The shell owns one
 * `useState` whose value is this record; each panel may portal into any of
 * the four regions via the `CompactPanel.*` sub-components below.
 */
export interface CompactPanelSlotTargets {
  body: HTMLElement | null
  header: HTMLElement | null
  headerActions: HTMLElement | null
  footer: HTMLElement | null
}

/**
 * Reactive className applied to the shell's `Dialog.Body`. `CompactPanel.Content`
 * can pass a className (e.g. `p-0`) that overrides the default body padding —
 * mirrors how `Panel.Body` accepts a className in non-compact mode.
 */
export interface CompactPanelBodyClassNameTarget {
  setBodyClassName: (className: string | undefined) => void
}

export const CompactPanelSlotsContext = createContext<CompactPanelSlotTargets>({
  body: null,
  header: null,
  headerActions: null,
  footer: null,
})

export const CompactPanelBodyClassNameContext = createContext<CompactPanelBodyClassNameTarget>({
  setBodyClassName: () => {},
})

/**
 * Reactive footer presence. `CompactPanel.Footer` registers itself on
 * mount so the shell only renders a `Dialog.Footer` region when the
 * active panel actually fills the footer slot. This avoids an empty
 * 16px footer bar + divider when a panel conditionally omits its
 * footer (e.g. the Comments panel when commenting is disabled).
 */
export interface CompactPanelFooterPresenceTarget {
  setHasFooter: (hasFooter: boolean) => void
}

export const CompactPanelFooterPresenceContext = createContext<CompactPanelFooterPresenceTarget>({
  setHasFooter: () => {},
})

interface SlotProps {
  children: ReactNode
}

interface ContentProps extends SlotProps {
  /**
   * Class applied to the shell's `Dialog.Body`. Use `p-0` for panels that
   * render full-width sections that should touch the dialog edges.
   */
  className?: string
}

/**
 * Portals the panel's main compact body content into the Dialog body
 * region. Outside the shell context the target is null and this renders
 * nothing. An optional `className` is forwarded to the shell's
 * `Dialog.Body` (e.g. `p-0` for flush panels).
 */
function Content({children, className}: ContentProps) {
  const {body} = use(CompactPanelSlotsContext)
  const {setBodyClassName} = use(CompactPanelBodyClassNameContext)
  useEffect(() => {
    setBodyClassName(className)
    return () => setBodyClassName(undefined)
  }, [className, setBodyClassName])
  return body ? createPortal(children, body) : null
}

/**
 * Portals into the secondary header region that sits inside
 * `Dialog.Header` below the title row — visually above the dialog
 * underline. Used for in-panel controls like the Comments filter row,
 * the Alerts filter row, the ManageBatch tabs, and the Comments
 * "Back to all comments" button in thread-detail mode.
 */
function Header({children}: SlotProps) {
  const {header} = use(CompactPanelSlotsContext)
  return header ? createPortal(children, header) : null
}

/**
 * Portals into the header region next to the close button. Used for panel
 * controls (Overview's edit pencil, CopilotChat's chat actions).
 */
function HeaderActions({children}: SlotProps) {
  const {headerActions} = use(CompactPanelSlotsContext)
  return headerActions ? createPortal(children, headerActions) : null
}

/**
 * Portals into the dialog footer region — rendered as a Primer
 * `Dialog.Footer` below the scrollable body, so panel action buttons
 * stay pinned while the body scrolls. Mounting this component publishes
 * footer presence to the shell so an empty `Dialog.Footer` bar is never
 * rendered for panels that omit their footer (e.g. read-only Comments).
 */
function Footer({children}: SlotProps) {
  const {footer} = use(CompactPanelSlotsContext)
  const {setHasFooter} = use(CompactPanelFooterPresenceContext)
  useEffect(() => {
    setHasFooter(true)
    return () => setHasFooter(false)
  }, [setHasFooter])
  return footer ? createPortal(children, footer) : null
}

/**
 * Compound component exposing the four portal slots that a side panel
 * can fill while rendered inside `CompactSidePanelDialog`:
 *
 *   <CompactPanel.HeaderActions>...</CompactPanel.HeaderActions>
 *   <CompactPanel.Header>...</CompactPanel.Header>
 *   <CompactPanel.Content>...</CompactPanel.Content>
 *   <CompactPanel.Footer>...</CompactPanel.Footer>
 *
 * Each sub-component is a thin portal wrapper that targets a specific
 * region inside the shared dialog shell.
 */
export const CompactPanel = {
  Content,
  Header,
  HeaderActions,
  Footer,
}
